package com.karawek.core.auth.stytch.client

import com.karawek.core.auth.model.NativeProvider
import com.karawek.core.auth.model.OAuthProvider
import com.karawek.core.auth.stytch.config.StytchConfig
import com.karawek.core.platform.PlatformContext
import kotlinx.browser.window
import me.tatarka.inject.annotations.Inject

class WebStytchClient @Inject constructor(
    private val config: StytchConfig,
) : StytchClient {

    override suspend fun initiateSmsOtp(phoneE164: String): InitiateOtpResponse =
        jsInitiateSmsOtp(
            publicToken = config.publicToken,
            phoneE164 = phoneE164,
            expirationMinutes = config.tokenExpirationMinutes,
        )

    override suspend fun initiateEmailOtp(email: String): InitiateOtpResponse =
        jsInitiateEmailOtp(
            publicToken = config.publicToken,
            email = email,
            expirationMinutes = config.tokenExpirationMinutes,
        )

    override suspend fun verifyOtp(methodId: String, otpCode: String): AuthenticationResponse =
        jsVerifyOtp(
            publicToken = config.publicToken,
            methodId = methodId,
            otpCode = otpCode,
            sessionDurationMinutes = config.sessionDurationMinutes,
        )

    override suspend fun authenticateWithNative(): AuthenticationResponse {
        throw UnsupportedOperationException("Native authentication is not supported on this platform.")
    }

    override suspend fun authenticateWithOAuth(provider: OAuthProvider): AuthenticationResponse {
        val baseUrl = window.location.href.substringBefore('?').substringBefore('#')
        val query = window.location.search
        val tokenType = parseQueryParam(query, "stytch_token_type")
        val token = parseQueryParam(query, "token")

        if (tokenType == "oauth" && token != null) {
            jsReplaceHistoryState(baseUrl)
            return jsOAuthAuthenticate(
                publicToken = config.publicToken,
                token = token,
                sessionDurationMinutes = config.sessionDurationMinutes,
            )
        }

        jsStartOAuth(
            publicToken = config.publicToken,
            providerName = when (provider) {
                OAuthProvider.Google -> "google"
                OAuthProvider.Apple -> "apple"
                OAuthProvider.Facebook -> "facebook"
            },
            loginRedirectUrl = baseUrl,
            signupRedirectUrl = baseUrl,
        )
        // Unreachable — jsStartOAuth redirects the browser away.
        error("OAuth redirect in progress")
    }

    override suspend fun signOut() {
        jsSignOut(config.publicToken)
    }

    override suspend fun currentSessionJwt(): String? =
        jsCurrentSessionJwt(config.publicToken)

    override suspend fun currentSessionToken(): String? =
        jsCurrentSessionToken(config.publicToken)

    override suspend fun renewSession(durationMinutes: Int) =
        jsRenewSession(config.publicToken, durationMinutes)

    override suspend fun registerPasskey() =
        jsRegisterPasskey(config.publicToken, config.passkeyDomain)

    override suspend fun authenticateWithPasskey(): AuthenticationResponse =
        jsAuthenticateWithPasskey(config.publicToken, config.sessionDurationMinutes)

    override fun supportsPasskeys(): Boolean = true

    override fun getNativeProviderSupport(): NativeProvider? = null
}

private fun parseQueryParam(query: String, key: String): String? =
    query.trimStart('?')
        .split('&')
        .firstNotNullOfOrNull { param ->
            val parts = param.split('=', limit = 2)
            parts[1].takeIf { parts.size == 2 && parts[0] == key }
        }

internal actual fun createStytchClient(
    platformContext: PlatformContext,
    config: StytchConfig,
): StytchClient = WebStytchClient(config = config)
